import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  ArrowPathIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

const ProfitabilityReport = ({ documents, products, sales, purchases }) => {
  const [loading, setLoading] = useState(false);
  const [profitabilityData, setProfitabilityData] = useState([]);
  const [timeRange, setTimeRange] = useState('month');
  const [summaryStats, setSummaryStats] = useState({
    profitable: 0,
    watchlist: 0,
    unprofitable: 0
  });
  const [isGenerating, setIsGenerating] = useState(false);
  
  const { companySettings } = useCompanySettings();
  
  useEffect(() => {
    calculateProfitability();
  }, [timeRange, products, sales, purchases]);
  
  const calculateProfitability = () => {
    setLoading(true);
    
    console.log('🧮 Profitability: Calculating with data:', {
      productsCount: products?.length || 0,
      salesCount: sales?.length || 0,
      purchasesCount: purchases?.length || 0,
      timeRange
    });
    
    // Calculer la rentabilité réelle à partir des données de produits et ventes
    setTimeout(() => {
      if (!products || products.length === 0 || !sales || sales.length === 0) {
        console.log('⚠️ Profitability: No data available');
        setProfitabilityData([]);
        setSummaryStats({ profitable: 0, watchlist: 0, unprofitable: 0 });
        setLoading(false);
        return;
      }
      
      // STEP 1: Calculate REAL-TIME costs from actual purchases
      const realTimeCosts = {};
      
      if (purchases && purchases.length > 0) {
        console.log('📦 Processing purchases for real-time costs...', {
          totalPurchases: purchases.length,
          purchasesWithItems: purchases.filter(p => p.items && p.items.length > 0).length
        });
        
        purchases.forEach(purchase => {
          if (purchase.items && Array.isArray(purchase.items)) {
            purchase.items.forEach(item => {
              const productId = item.id || item.productId;
              const quantity = parseInt(item.quantity) || 1;
              const unitCost = parseFloat(item.price) || parseFloat(item.unitPrice) || 0;
              
              // Validation: Ensure we have valid data
              if (!productId || unitCost <= 0 || quantity <= 0) {
                console.warn('⚠️ Invalid purchase item data:', {
                  productId,
                  unitCost,
                  quantity,
                  item
                });
                return;
              }
              
              if (!realTimeCosts[productId]) {
                realTimeCosts[productId] = {
                  totalCost: 0,
                  totalQuantity: 0,
                  averageCost: 0,
                  lastPurchaseDate: null,
                  purchaseCount: 0
                };
              }
              
              realTimeCosts[productId].totalCost += (quantity * unitCost);
              realTimeCosts[productId].totalQuantity += quantity;
              realTimeCosts[productId].averageCost = realTimeCosts[productId].totalCost / realTimeCosts[productId].totalQuantity;
              realTimeCosts[productId].lastPurchaseDate = purchase.date || purchase.createdAt;
              realTimeCosts[productId].purchaseCount += 1;
              
              console.log(`📊 Purchase processed for ${productId}:`, {
                quantity,
                unitCost,
                totalCost: quantity * unitCost,
                runningAverage: realTimeCosts[productId].averageCost.toFixed(2)
              });
            });
          }
        });
        
        console.log('💰 FINAL Real-time costs calculated:', realTimeCosts);
      } else {
        console.log('⚠️ No purchase data available for real-time cost calculation');
      }
      
      // STEP 2: Calculer la rentabilité par produit
      const productProfitability = {};
      
      // Traiter toutes les ventes pour calculer les revenus par produit
      sales.forEach(sale => {
        if (sale.items && Array.isArray(sale.items)) {
          sale.items.forEach(item => {
            const productId = item.id || item.productId;
            const product = products.find(p => p.id === productId);
            
            if (product) {
              if (!productProfitability[productId]) {
                // PRIORITY: Use REAL-TIME cost from purchases, then fallback to product data
                let productCost = 0;
                let costSource = 'no_data';
                
                // STEP 1: Try real-time purchases first (most accurate)
                if (realTimeCosts[productId] && realTimeCosts[productId].averageCost > 0) {
                  productCost = realTimeCosts[productId].averageCost;
                  costSource = 'purchases_real_time';
                } 
                // STEP 2: Try product.cost field (server data)
                else if (product.cost && product.cost > 0 && product.cost !== product.price) {
                  productCost = product.cost;
                  costSource = 'product_cost_server';
                } 
                // STEP 3: Try product.costPrice field (form data)
                else if (product.costPrice && product.costPrice > 0 && product.costPrice !== product.price) {
                  productCost = product.costPrice;
                  costSource = 'product_costPrice_form';
                }
                // STEP 4: Validation - Ensure cost is NOT equal to selling price
                if (productCost === product.price) {
                  console.warn(`⚠️ Cost equals selling price for ${product.name} - resetting to 0`);
                  productCost = 0;
                  costSource = 'invalid_cost_equals_price';
                }
                
                console.log(`💰 FIXED Cost Debug for ${product.name}:`, {
                  productId,
                  costSource,
                  realTimeCost: realTimeCosts[productId]?.averageCost || 'N/A',
                  serverCost: product.cost || 'N/A',
                  formCostPrice: product.costPrice || 'N/A',
                  sellingPrice: product.price || 'N/A',
                  finalCost: productCost,
                  isValid: productCost !== product.price,
                  purchaseHistory: realTimeCosts[productId] || 'No purchases'
                });
                
                productProfitability[productId] = {
                  name: product.name,
                  revenue: 0,
                  cost: 0,
                  quantitySold: 0,
                  costPrice: productCost,
                  sellingPrice: product.price || 0,
                  costSource: costSource
                };
              }
              
              const quantity = parseInt(item.quantity) || 1;
              const itemTotal = parseInt(item.total) || (quantity * (parseInt(item.price) || parseInt(item.unitPrice) || 0));
              
              // Use the stored costPrice (which could be from multiple sources)
              const itemCost = quantity * productProfitability[productId].costPrice;
              
              // VALIDATION: Ensure revenue is not equal to cost (data integrity check)
              const isDataValid = itemTotal !== itemCost && productProfitability[productId].costPrice !== (itemTotal / quantity);
              
              console.log(`📊 FIXED Sale Item Debug for ${product.name}:`, {
                quantity,
                saleUnitPrice: parseInt(item.price) || parseInt(item.unitPrice) || 0,
                itemTotal,
                unitCost: productProfitability[productId].costPrice,
                itemCost,
                revenue: itemTotal,
                profit: itemTotal - itemCost,
                margin: itemTotal > 0 ? ((itemTotal - itemCost) / itemTotal * 100).toFixed(1) + '%' : '0%',
                isDataValid,
                costSource: productProfitability[productId].costSource
              });
              
              if (!isDataValid) {
                console.warn(`⚠️ Potential data issue for ${product.name}: Revenue equals cost or cost equals unit price`);
              }
              
              productProfitability[productId].revenue += itemTotal;
              productProfitability[productId].cost += itemCost;
              productProfitability[productId].quantitySold += quantity;
            }
          });
        }
      });
      
      // Convertir en format pour le graphique
      const realData = Object.values(productProfitability)
        .map(product => {
          const profit = product.revenue - product.cost;
          const margin = product.revenue > 0 ? (profit / product.revenue) * 100 : 0;
          
          return {
            name: product.name,
            revenue: product.revenue,
            cost: product.cost,
            profit: profit,
            margin: parseFloat(margin.toFixed(1))
          };
        })
        .filter(product => product.revenue > 0) // Exclure les produits sans ventes
        .sort((a, b) => b.profit - a.profit) // Trier par profit décroissant
        .slice(0, 10); // Top 10 des produits les plus rentables
      
      // Calculer les statistiques de résumé en temps réel
      const stats = realData.reduce((acc, product) => {
        if (product.margin > 40) {
          acc.profitable++;
        } else if (product.margin > 25) {
          acc.watchlist++;
        } else {
          acc.unprofitable++;
        }
        return acc;
      }, { profitable: 0, watchlist: 0, unprofitable: 0 });
      
      console.log('📊 Profitability: Calculated data:', {
        totalProducts: realData.length,
        stats,
        sampleProduct: realData[0]
      });
      
      setProfitabilityData(realData);
      setSummaryStats(stats);
      setLoading(false);
    }, 500);
  };

  const handleGenerateReport = async () => {
    if (isGenerating) return;
    
    setIsGenerating(true);
    console.log('🔄 Profitability: Starting PDF generation...');
    
    try {
      console.log('📊 Data check:', {
        profitabilityData: profitabilityData?.length || 0,
        summaryStats,
        sampleProduct: profitabilityData[0]
      });
      
      // Minimalist red theme colors (EXACTLY like Sales)
      const primaryRed = [185, 28, 28]; // #B91C1C - True red color
      const secondaryRed = [153, 27, 27]; // #991B1B - Darker red
      const lightGray = [248, 248, 248]; // Very light gray for subtle backgrounds
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentPage = 1;
      
      // Helper function to add minimal header and footer (EXACTLY like Sales)
      const addHeaderFooter = async () => {
        // HEADER - Company logo and name
        let headerYPosition = 10;
        
        // Add company logo if available
        if (companySettings?.logo) {
          try {
            console.log('📄 Profitability: Adding company logo to PDF...');
            
            // Add logo to PDF (small size: 15mm width, proportional height)
            pdf.addImage(companySettings.logo, 'PNG', 20, 5, 15, 10);
            
            // Position company name next to logo
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 40, 12);
            
            headerYPosition = 18; // Adjust for logo height
          } catch (error) {
            console.log('Error adding logo to PDF:', error);
            // Fallback to text-only header
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 20, 10);
          }
        } else {
          // No logo - just company name
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0); // Black color
          const companyName = companySettings?.companyName || 'INVENTAIRE V2';
          pdf.text(companyName, 20, 10);
        }
        
        // Simple thin line below header
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.2);
        pdf.line(20, headerYPosition, pageWidth - 20, headerYPosition);
        
        // FOOTER - Software name and page number
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(120, 120, 120);
        pdf.text('INVENTAIRE V2', 20, pageHeight - 8);
        
        // Page number
        pdf.text(`${currentPage}`, pageWidth - 25, pageHeight - 8);
      };
      
      // Helper function to add minimal section header (EXACTLY like Sales)
      const addSectionHeader = async (title) => {
        if (yPosition > 250) {
          pdf.addPage();
          currentPage++;
          await addHeaderFooter();
          yPosition = 25; // Adjust for new header size
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0); // Black color
        pdf.text(title, 20, yPosition);
        yPosition += 5;
        
        // Simple clean line
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.5);
        pdf.line(20, yPosition, 80, yPosition);
        
        yPosition += 10;
        
        // Reset to black for content
        pdf.setTextColor(0, 0, 0);
      };
      
      // Add header/footer to first page
      await addHeaderFooter();
      let yPosition = 30; // Start below larger header with logo
      
      // 1. MINIMAL TITLE (EXACTLY like Sales)
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Black color
      pdf.text('RAPPORT DE RENTABILITÉ', 20, yPosition);
      yPosition += 10;
      
      // Simple date info
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 20, yPosition);
      yPosition += 15;
      
      // Calculate totals for summary
      const totalProducts = profitabilityData.length;
      const avgMargin = totalProducts > 0 ? 
        profitabilityData.reduce((sum, p) => sum + (p.margin || 0), 0) / totalProducts : 0;
      const totalProfit = profitabilityData.reduce((sum, p) => sum + (p.profit || 0), 0);
      
      // Summary data (EXACTLY like Sales format)
      const summaryData = [
        ['Nombre total de produits analysés', totalProducts.toString()],
        ['Profit total', `${totalProfit.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`],
        ['Marge bénéficiaire moyenne', `${avgMargin.toFixed(1)}%`],
        ['Produits rentables (Marge > 40%)', summaryStats.profitable.toString()],
        ['Période d\'analyse', 'Toutes les données disponibles']
      ];
      
      console.log('📄 Profitability: About to create summary table with data:', summaryData);
      try {
        autoTable(pdf, {
          startY: yPosition,
          head: [['Métrique', 'Valeur']],
          body: summaryData,
        theme: 'plain',
        headStyles: { 
          fillColor: primaryRed, 
          textColor: 255, 
          fontSize: 9, 
          fontStyle: 'bold',
          halign: 'left'
        },
        styles: { 
          fontSize: 8, 
          cellPadding: 3,
          lineColor: [220, 220, 220],
          lineWidth: 0.1
        },
        margin: { left: 20, right: 20 }
      });
      console.log('✅ Profitability: Summary table created successfully');
      } catch (tableError) {
        console.error('❌ Profitability: Error creating summary table:', tableError);
        throw tableError;
      }
      
      yPosition = pdf.lastAutoTable.finalY + 10;
      
      // Add section header for detailed data (EXACTLY like Sales)
      await addSectionHeader('ANALYSE DÉTAILLÉE PAR PRODUIT');
      
      // Top 10 most profitable products table (limited like Sales)
      const top10ProfitableData = profitabilityData
        .slice(0, 10) // Top 10 only
        .map(product => [
          product.name || '',
          `${(product.revenue || 0).toLocaleString('fr-FR').replace(/\s/g, ',')} DA`,
          `${(product.cost || 0).toLocaleString('fr-FR').replace(/\s/g, ',')} DA`,
          `${(product.profit || 0).toLocaleString('fr-FR').replace(/\s/g, ',')} DA`,
          `${(product.margin || 0).toFixed(1)}%`
        ]);
      
      console.log('📄 Profitability: Top products data for PDF:', top10ProfitableData.length, 'products');
      try {
        autoTable(pdf, {
          startY: yPosition,
          head: [['Produit', 'CA', 'Coût', 'Profit', 'Marge (%)']],
          body: top10ProfitableData,
          theme: 'plain',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 9, 
            fontStyle: 'bold',
            halign: 'left'
          },
          styles: { 
            fontSize: 8, 
            cellPadding: 3,
            lineColor: [220, 220, 220],
            lineWidth: 0.1
          },
          margin: { left: 20, right: 20 }
        });
        console.log('✅ Profitability: Products table created successfully');
      } catch (tableError) {
        console.error('❌ Profitability: Error creating products table:', tableError);
        throw tableError;
      }
      
      // Save the PDF (EXACTLY like Sales filename format)
      const fileName = `rapport-rentabilite-${new Date().toISOString().split('T')[0]}.pdf`;
      console.log('📄 Profitability: About to save PDF with filename:', fileName);
      pdf.save(fileName);
      console.log('📄 Profitability: PDF save command executed');
      
      setIsGenerating(false);
      console.log('✅ PDF generation completed successfully');
      
    } catch (error) {
      console.error('❌ Profitability: Error generating PDF:', error);
      alert('Erreur lors de la génération du rapport PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-gray-900/50 backdrop-blur-md p-6 rounded-2xl border border-white/10 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h2 className="text-xl font-semibold text-white flex items-center">
          <ChartBarIcon className="h-6 w-6 mr-2 text-red-500" />
          Rapport de Rentabilité
        </h2>
        
        <div className="flex items-center space-x-3">

          
          <button 
            onClick={handleGenerateReport}
            disabled={isGenerating}
            className={`px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors flex items-center gap-2 ${
              isGenerating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            {isGenerating ? 'Génération...' : 'Exporter PDF Complet'}
          </button>
          
          <button 
            onClick={calculateProfitability}
            className={`p-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors ${loading ? 'animate-spin' : ''}`}
          >
            <ArrowPathIcon className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-r from-green-500/20 to-green-700/20 p-4 rounded-xl border border-green-500/30">
          <h3 className="text-sm text-green-400 mb-1">Produits Rentables</h3>
          <p className="text-2xl font-semibold text-white">{summaryStats.profitable}</p>
          <p className="text-xs text-white/60">Marge &gt; 40%</p>
        </div>
        
        <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-700/20 p-4 rounded-xl border border-yellow-500/30">
          <h3 className="text-sm text-yellow-400 mb-1">Produits en Surveillance</h3>
          <p className="text-2xl font-semibold text-white">{summaryStats.watchlist}</p>
          <p className="text-xs text-white/60">Marge entre 25% et 40%</p>
        </div>
        
        <div className="bg-gradient-to-r from-red-500/20 to-red-700/20 p-4 rounded-xl border border-red-500/30">
          <h3 className="text-sm text-red-400 mb-1">Produits Non Rentables</h3>
          <p className="text-2xl font-semibold text-white">{summaryStats.unprofitable}</p>
          <p className="text-xs text-white/60">Marge &lt; 25%</p>
        </div>
      </div>
      
      <div className="bg-white/5 p-4 rounded-xl mb-6">
        <h3 className="text-lg font-medium text-white mb-4">Rentabilité par Produit</h3>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={profitabilityData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" />
              <YAxis stroke="rgba(255,255,255,0.6)" />
              <Tooltip 
                contentStyle={{ backgroundColor: 'rgba(17,24,39,0.9)', borderColor: 'rgba(255,255,255,0.1)', color: '#fff' }}
              />
              <Legend />
              <Bar dataKey="revenue" name="Revenu (DA)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cost" name="Coût (DA)" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="profit" name="Profit (DA)" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      <div className="bg-white/5 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-white/10">
          <thead className="bg-white/5">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                Produit
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-white/60 uppercase tracking-wider">
                Revenu (DA)
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-white/60 uppercase tracking-wider">
                Coût (DA)
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-white/60 uppercase tracking-wider">
                Profit (DA)
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-white/60 uppercase tracking-wider">
                Marge (%)
              </th>
            </tr>
          </thead>
          <tbody className="bg-white/0 divide-y divide-white/10">
            {profitabilityData.map((item, index) => (
              <tr key={index} className="hover:bg-white/5">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                  {item.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-white">
                  {item.revenue.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-white">
                  {item.cost.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-white">
                  {item.profit.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                  <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                    item.margin > 40 ? 'bg-green-500/20 text-green-400' :
                    item.margin > 25 ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {item.margin}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProfitabilityReport; 